<script lang="ts">
	import PageHeader from '$lib/components/PageHeader.svelte';
	import DemoVideo from '$lib/components/DemoVideo.svelte';
	import ThemeImage from '$lib/components/ThemeImage.svelte';
	import {
		Timer,
		LayoutGrid,
		Tags,
		ScrollText,
		Calendar,
		PieChart,
		Stethoscope,
		Settings,
		ArrowRight
	} from '@lucide/svelte';

	// Mirrors the final page of the in-app walkthrough ("Where things live"):
	// one entry per real surface, same order, same story. Grouped the way the
	// surfaces are actually placed — the popover lives in the menu bar, and
	// everything else is a tab of the one Moment Tally window — so the page
	// structure answers its own title. Surfaces with a `video` (the popover,
	// launcher, history, label review) render as live captures; the rest are
	// theme-swapped stills — see the loop below.
	interface Surface {
		icon: typeof Timer;
		name: string;
		blurb: string;
		image?: string;
		imageLight?: string;
		video?: string;
		portrait?: boolean;
	}

	const menuBar: Surface[] = [
		{
			icon: Timer,
			name: 'Menu-bar popover',
			blurb:
				'Start and stop timers, add notes or change labels on the fly. Quick-start rows reveal their quick labels on hover, so switching contexts takes a couple of clicks.',
			video: '/screenshots/popover.mp4',
			portrait: true
		}
	];

	const windowTabs: Surface[] = [
		{
			icon: LayoutGrid,
			name: 'Launcher',
			blurb:
				'Saved label sets as one-click cards — icons and colors for the way you actually organize work. Sets that are already running light up.',
			video: '/screenshots/launcher.mp4'
		},
		{
			icon: Tags,
			name: 'Label Sets',
			blurb:
				'Name and edit the sets behind one-click timers. Set up quick labels to fit your workflow, and give every key: value pair its own color.',
			image: '/screenshots/label-sets.webp',
			imageLight: '/screenshots/label-sets-light.webp'
		},
		{
			icon: ScrollText,
			name: 'Log',
			blurb:
				'Review and freely edit timespans after-the-fact: because real work overlaps, gets interrupted, and needs correcting.',
			image: '/screenshots/log.webp',
			imageLight: '/screenshots/log-light.webp'
		},
		{
			icon: Calendar,
			name: 'Calendar',
			blurb:
				'Your week laid out hour by hour. Overlapping timers share columns, so multi-tasking is visible instead of hidden.',
			image: '/screenshots/calendar.webp',
			imageLight: '/screenshots/calendar-light.webp'
		},
		{
			icon: PieChart,
			name: 'History',
			blurb:
				'Visualize your timespans with group-by based aggregations — pick any label key and see where the week actually went.',
			video: '/screenshots/history.mp4'
		},
		{
			icon: Stethoscope,
			name: 'Label Review',
			blurb:
				"Merge drifted keys and stray values. With Moment Tally it's easy to catch these outliers and correct them before they impact downstream.",
			video: '/screenshots/label-review.mp4'
		},
		{
			icon: Settings,
			name: 'Settings',
			blurb:
				'Sync, colours, and everything else. Local-first storage with one-click JSON export — and a demo mode that never touches your real data.',
			image: '/screenshots/settings.webp',
			imageLight: '/screenshots/settings-light.webp'
		}
	];

	const surfaceGroups = [
		{ place: 'In the menu bar', surfaces: menuBar },
		{ place: 'In the Moment Tally window', surfaces: windowTabs }
	];
</script>

<svelte:head>
	<title>Features — Moment Tally</title>
	<meta
		name="description"
		content="A tour of every Moment Tally surface: the menu-bar popover, launcher, label sets, log, calendar, history charts, label review, and settings — plus the interactive onboarding that teaches the label model."
	/>
</svelte:head>

<PageHeader
	eyebrow=""
	title="Where things live"
	lead="Every part of the label model has a real surface in the app. Here's the tour — starting with the walkthrough that teaches it."
/>

<!-- ============================= ONBOARDING ============================= -->
<section class="mx-auto max-w-6xl px-5 py-20">
	<div class="grid items-center gap-10 lg:grid-cols-[0.85fr_1.15fr]">
		<div>
			<!-- <p class="font-mono text-xs uppercase tracking-widest text-accent">First launch</p> -->
			<h2 class="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
				An onboarding that teaches the model
			</h2>
			<p class="mt-4 leading-relaxed text-fg-muted">
				Moment Tally's interactive walkthrough hands you a live demo week and lets you break it.
				Group spans by any label, corrupt the charts with a bad schema and heal them again, then
				pick starter label sets that become real one-click timers.
			</p>
			<p class="mt-3 leading-relaxed text-fg-muted">
				Seven pages later you haven't read a manual — you've already used every idea in the app.
			</p>
		</div>
		<figure class="card overflow-hidden">
			<DemoVideo
				src="/screenshots/onboarding.mp4"
				label="The Moment Tally interactive walkthrough: grouping a demo week by label, corrupting and healing a label schema, and picking starter label sets"
				width={1400}
				height={1184}
				class="w-full"
			/>
		</figure>
	</div>
</section>

<!-- ============================= SURFACES ============================= -->
<section class="border-t border-border/70 bg-bg-raised/40">
	<div class="mx-auto max-w-6xl px-5 py-20">
		<div class="max-w-2xl">
			<!-- <p class="font-mono text-xs uppercase tracking-widest text-accent">The surfaces</p> -->
			<h2 class="mt-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
				Eight views, one label model
			</h2>
			<p class="mt-4 text-fg-muted">
				Every capture on this page is the app's own demo mode — seeded sample data, unretouched UI.
			</p>
		</div>

		{#each surfaceGroups as g, gi (g.place)}
			<div class="mt-16 flex items-center gap-4" role="presentation">
				<p class="font-mono text-xs uppercase tracking-widest text-fg-faint">{g.place}</p>
				<div class="h-px flex-1 bg-border"></div>
			</div>

			<div class="mt-12 space-y-16">
				{#each g.surfaces as s, i (s.name)}
					<article
						class="grid items-center gap-8 lg:grid-cols-2 {(gi + i) % 2 === 1
							? 'lg:[&>figure]:order-first'
							: ''}"
					>
						<div class={s.portrait ? 'lg:pr-8' : ''}>
							<div
								class="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-surface/60 text-accent"
							>
								<s.icon size={20} />
							</div>
							<h3 class="font-display text-2xl font-bold">{s.name}</h3>
							<p class="mt-3 leading-relaxed text-fg-muted">{s.blurb}</p>
						</div>
						<figure class="card overflow-hidden {s.portrait ? 'mx-auto w-full max-w-xs' : ''}">
							{#if s.video}
								<DemoVideo
									src={s.video}
									label={s.name}
									width={s.portrait ? 800 : 1400}
									height={s.portrait ? 1688 : 1192}
									class="w-full"
								/>
							{:else if s.image}
								<ThemeImage
									dark={s.image}
									light={s.imageLight}
									alt="Moment Tally {s.name} view"
									width={1784}
									height={1520}
									class="h-auto w-full"
								/>
							{/if}
						</figure>
					</article>
				{/each}
			</div>
		{/each}
	</div>
</section>

<!-- ============================= CTA ============================= -->
<section class="mx-auto max-w-6xl px-5 py-20 text-center">
	<h2 class="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
		See it on your own menu bar
	</h2>
	<p class="mx-auto mt-4 max-w-xl text-lg text-fg-muted">
		Everything above works locally from the first launch — no account, no server, no setup.
	</p>
	<div class="mt-8 flex flex-wrap justify-center gap-3">
		<a
			href="/docs#install"
			class="btn-accent rounded-full px-6 py-3 text-sm font-semibold text-white transition-transform hover:scale-105"
		>
			Get Moment Tally
		</a>
		<a
			href="/case-study"
			class="inline-flex items-center gap-1.5 rounded-full border border-border-strong px-6 py-3 text-sm font-medium text-fg-muted transition-colors hover:border-accent hover:text-fg"
		>
			Read the case studies <ArrowRight size={15} />
		</a>
	</div>
</section>
